import { useId } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { LOCALE_NAMES, SUPPORTED_LOCALES, isLocale, type Locale } from '@platform/shared';
import { api } from '../api/client';
import { ME_KEY } from '../auth/useMe';
import { useLocale } from '../i18n';
import { common } from '../i18n/messages';

/**
 * The language switcher (shell footer and sign-in page). Changes the UI at once; when a person is
 * signed in it also persists to people.locale so every device follows. Language names are shown in
 * their own language on purpose (a French speaker looking for « Français » should not need English).
 */
export function LocaleSwitcher({ signedIn, compact }: { signedIn: boolean; compact?: boolean }) {
  const intl = useIntl();
  const id = useId();
  const { locale, setLocale } = useLocale();
  const qc = useQueryClient();
  const persist = useMutation({
    mutationFn: (next: Locale) => api.updateMe({ locale: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_KEY }),
  });

  const current = isLocale(locale) ? locale : 'en';
  return (
    <div className={compact ? 'field inline' : 'field'}>
      <label htmlFor={id} className={compact ? 'sr-only' : undefined}>
        {intl.formatMessage(common.language)}
      </label>
      <select
        id={id}
        className="input select"
        value={current}
        onChange={(e) => {
          const next = e.target.value;
          if (!isLocale(next)) return;
          setLocale(next);
          if (signedIn) persist.mutate(next);
        }}
      >
        {SUPPORTED_LOCALES.map((l) => (
          <option key={l} value={l} lang={l}>
            {LOCALE_NAMES[l]}
          </option>
        ))}
      </select>
    </div>
  );
}
